import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES,
  autopilotCreditsShortfall,
  type BlogAutogen,
  type BlogTenantStatus,
} from '@/store/slices/blog';
import type { AutopilotByline } from '../../byline';
import { Toggle } from '../../../../components/Toggle';
import { Modal } from '../../../../components/Modal';
import { Button } from '../../../../components/Button';
import { LumioMark } from '../../../creatives/components/LumioMark';
import { FtuxTarget } from '../../../../features/ftux/FtuxTarget';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { languageName } from '../../../../config/languageEndonyms';
import { toAppLang } from '../../../../config/langCodeMap';
import { dateLocale } from '../../../../utils/locale';
import { cadenceCopy } from './cadence';
import {
  Root,
  Row,
  Words,
  TitleLine,
  Title,
  CopyLine,
  Copy,
  NextLine,
  PausedLine,
  ReasonWrap,
  ModalLine,
} from './AutopilotPanel.styles';

/**
 * How the next run reads: "Oct 2, 1:40 PM" in the reader's own timezone and in
 * the UI language, the way the rest of the area localizes a date.
 */
const NEXT_RUN_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

/**
 * The next run, or null when there is none to announce. An unparseable
 * timestamp is no line at all: `Intl.DateTimeFormat#format` throws on NaN
 * rather than printing "Invalid Date".
 */
const formatNextRun = (iso: string | null | undefined, format: Intl.DateTimeFormat): string | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return format.format(ms);
};

/*
 * Where the FTUX's Autopilot piece lights the panel, its switch marked inside it (inert outside an FTUX step). The
 * shared Panel and Toggle forward no attribute: a plain block round the panel carries its mark (the page lays it out
 * as it laid out the panel), a span round the switch carries the switch's.
 */
const AUTOPILOT_TARGET: FtuxTargetId = 'blog-autopilot';

export type AutopilotPanelProps = {
  /** The endpoint's answer, or null while it has not answered yet. */
  autogen: BlogAutogen | null;
  /** Why the settings could not be read, when they could not. */
  error: string | null;
  /** The blog's own switch, which Autopilot can only run behind. */
  tenantStatus: BlogTenantStatus | null;
  /**
   * Credits per article from the pricing table, when it has loaded. The
   * endpoint's own price is the fallback; with neither, the copy names no
   * number.
   */
  price: number | undefined;
  /**
   * The owner's AI-credits balance, as the header chip shows it; null or
   * absent until the wallet has loaded. The credits notice stays open on the
   * service until a run succeeds or something closes it, so the panel only
   * says "Paused" while this is short of what the next article costs.
   */
  creditsBalance?: number | null;
  /**
   * The byline turning on will send, and which sentence of the confirmation
   * says so (`autopilotByline`): undefined while the preferences or the blog's
   * article list it is decided from have not loaded. An OFF switch waits on
   * undefined, so the byline the confirmation promises is the one that goes
   * out. An ON switch does not: turning off sends no byline, so it never
   * waits on one (a list that failed to load must not keep paid runs going).
   */
  byline: AutopilotByline | undefined;
  /** A write is in flight (the page's shared write lock). */
  busy: boolean;
  /** The owner's decision, after the confirmation when turning on. */
  onChange: (enabled: boolean) => void;
  /** The switch opened the confirmation (the FTUX's Autopilot piece ends there). */
  onConfirmOpen?: () => void;
};

/**
 * The Autopilot switch (`docs/auto-generation.md` in spark-blog): on the
 * cadence the service sets (two days by default, minutes in a test
 * environment) the blog writes and publishes an article of its own, paid from
 * the owner's AI credits.
 *
 * What it shows depends on two answers, the blog's switch and the Autopilot
 * settings, and on which of them is missing:
 *
 * - the settings answered `{ supported: false }` (an older service build):
 *   nothing at all, so the studio can ship ahead of the service;
 * - the settings have not answered and have not failed, the blog's own
 *   switch has not answered, or, while Autopilot is off, the byline is not
 *   known yet (the preferences or the article list are still loading): the
 *   switch is locked while it reads, so the owner never acts on a
 *   placeholder. The service accepts a switch behind a blog that is off (it
 *   pauses the runs rather than refuse), so this lock is what keeps the
 *   "blog first" reason honest. Turning off sends no byline, so an ON switch
 *   never waits on one;
 * - the blog is suspended, off, or never turned on; the read failed; or the
 *   service says Autopilot is not `available` here: locked, with the reason
 *   on the wrapper (a disabled switch surfaces no tooltip of its own);
 * - otherwise operable. Turning ON asks first, naming the cost, the cadence
 *   and the byline, because it starts spending credits within the hour;
 *   turning OFF is immediate.
 */
export const AutopilotPanel = ({
  autogen,
  error,
  tenantStatus,
  price,
  creditsBalance = null,
  byline,
  busy,
  onChange,
  onConfirmOpen,
}: AutopilotPanelProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  // The app's language is a locale FOLDER name, not a BCP-47 tag, so it goes
  // through `dateLocale`. moment's global locale used to format this line, and
  // it disagreed with the UI language ("out 1" inside English copy). Rebuilt
  // when the language changes, which re-renders this component.
  const locale = dateLocale(i18n.language);
  const nextRunFormat = React.useMemo(() => new Intl.DateTimeFormat(locale, NEXT_RUN_FORMAT), [locale]);

  if (autogen !== null && !autogen.supported) return null;

  const settings = autogen !== null && autogen.supported ? autogen : null;
  const on = settings?.enabled === true;

  const suspended = tenantStatus?.banned === true;
  // Never turned on, or turned off: both are "not live" (an unprovisioned
  // tenant is never enabled), and both are fixed by the switch beside the page
  // title. Unknown (still reading) is neither.
  const blogOff = tenantStatus !== null && !suspended && tenantStatus.enabled !== true;
  // Any of the reads still in flight: the settings (no answer and no
  // failure), the blog's own switch, or what the byline is decided from. The settings
  // alone must never unlock the toggle: the service does not refuse a switch
  // behind a blog that is off, so a panel that read its settings before the
  // tenant would let the owner turn on runs the scheduler only skips. A tenant
  // read that FAILED keeps this lock too; the blog switch beside the page
  // title is showing that failure and its retry. The byline only matters to
  // turning ON (the confirmation names it and the enable sends it); turning
  // off sends the switch alone, so an ON switch never waits on it, and an
  // article list that failed to load cannot keep paid runs going.
  const bylinePending = !on && byline === undefined;
  const checking = (autogen === null && error === null) || tenantStatus === null || bylinePending;
  // A read that failed. The copy is the service's own, already a locale key.
  const failure = autogen === null ? error : null;
  const unavailable = settings !== null && !settings.available;

  const reason = suspended
    ? t('Auto-generation is not available while the blog is suspended')
    : blogOff
      ? t("This brand's blog is not turned on yet. Use the switch beside the page title.")
      : failure !== null
        ? t(failure)
        : unavailable
          ? t('Autopilot is not available for this blog.')
          : undefined;
  // Turning off sends the switch alone and the service never refuses it, so
  // nothing that stops an owner from turning Autopilot ON may trap them with it
  // on: once the settings say it is on, only a write in flight locks it.
  const canTurnOff = on && settings !== null;
  const locked = busy || (!canTurnOff && (checking || reason !== undefined));

  // The endonym, rendered raw like every language picker (never through t()).
  // The service names the casino's first language; empty is English, as the
  // service itself reads it.
  const language = languageName(toAppLang(settings?.language || 'en'));
  // The cadence the service runs on, said in the unit that fits: days for the
  // production default, minutes for a test environment (`cadence.ts`). The
  // contract's default stands in while the settings are still being read.
  const cadence = cadenceCopy(t, settings?.intervalMinutes ?? BLOG_AUTOGEN_DEFAULT_INTERVAL_MINUTES);
  const cost = price ?? settings?.pricePerArticle ?? undefined;
  const nextRun = on ? formatNextRun(settings?.nextRunAt, nextRunFormat) : null;
  // A refused run's notice, while the balance is still short of the next
  // article: what it needs, or null. A top-up the service has not heard of yet
  // (the layout closes the notice once it sees one) is not "paused".
  const paused = on
    ? autopilotCreditsShortfall({ needed: settings?.creditsNeeded ?? null, balance: creditsBalance, price: cost })
    : null;

  const openConfirm = (): void => {
    setConfirmOpen(true);
    onConfirmOpen?.();
  };
  const confirm = (): void => {
    setConfirmOpen(false);
    onChange(true);
  };

  return (
    <>
      <div data-ftux={AUTOPILOT_TARGET}>
        <Root>
          <Row data-autopilot="panel" data-autopilot-on={on ? 'true' : 'false'}>
            <Words>
              <TitleLine>
                <LumioMark size={16} />
                <Title text="Autopilot" tag="span" />
              </TitleLine>
              {failure !== null ? (
                <Copy text={failure} tag="span" />
              ) : (
                <CopyLine>
                  <Copy
                    text="{{cadence}}, a new article is written and published in {{language}}."
                    placeholders={{ cadence, language }}
                    tag="span"
                  />{' '}
                  {/* Each branch carries its own literal so every key is extracted. */}
                  {cost === undefined ? (
                    <Copy text="Each article is paid with your AI credits." tag="span" />
                  ) : cost === 1 ? (
                    <Copy text="Each article costs {{count}} credit." placeholders={{ count: cost }} tag="span" />
                  ) : (
                    <Copy text="Each article costs {{count}} credits." placeholders={{ count: cost }} tag="span" />
                  )}
                </CopyLine>
              )}
              {nextRun !== null ? (
                <NextLine text="Next article published around {{when}}" placeholders={{ when: nextRun }} tag="span" />
              ) : null}
              {paused !== null ? (
                paused === 1 ? (
                  <PausedLine
                    text="Paused: the next article needs {{count}} credit. Top up to continue."
                    placeholders={{ count: paused }}
                    tag="span"
                  />
                ) : (
                  <PausedLine
                    text="Paused: the next article needs {{count}} credits. Top up to continue."
                    placeholders={{ count: paused }}
                    tag="span"
                  />
                )
              ) : null}
            </Words>
            <ReasonWrap title={locked ? reason : undefined}>
              <FtuxTarget id="blog-autopilot-switch">
                <Toggle
                  on={on}
                  label="Turn Autopilot on or off"
                  disabled={locked}
                  onToggle={() => (on ? onChange(false) : openConfirm())}
                />
              </FtuxTarget>
            </ReasonWrap>
          </Row>
        </Root>
      </div>

      {/* Turning on starts spending credits within the hour, so it says what it
          will cost, how often, and under whose name, before anything runs. */}
      <Modal
        open={confirmOpen}
        title={t('Turn Autopilot on?')}
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <Button label={t('Cancel')} variant="outline" onClick={() => setConfirmOpen(false)} />
            <Button label={t('Turn on')} disabled={busy} onClick={confirm} />
          </>
        }
      >
        <ModalLine
          text="{{cadence}}, a new article is written and published on your blog in {{language}}. The first one is written within about an hour."
          placeholders={{ cadence, language }}
          tag="paragraph"
        />
        {cost === undefined ? (
          <ModalLine text="Each article is charged to your AI credits when it is written." tag="paragraph" />
        ) : cost === 1 ? (
          <ModalLine
            text="Each article costs {{count}} credit, charged to your AI credits when it is written."
            placeholders={{ count: cost }}
            tag="paragraph"
          />
        ) : (
          <ModalLine
            text="Each article costs {{count}} credits, charged to your AI credits when it is written."
            placeholders={{ count: cost }}
            tag="paragraph"
          />
        )}
        {/* Exactly what the enable will do with the byline. The dialog cannot
            open while the byline is still unknown (the switch is locked then).
            Without a name to send, the stored byline is cleared on the way out
            and the service signs with the latest article that has one, if any. */}
        {byline?.copy === 'signed' ? (
          <ModalLine text="Articles will be signed by {{name}}." placeholders={{ name: byline.author.name }} tag="paragraph" />
        ) : byline?.copy === 'latest-article' ? (
          <ModalLine text="Articles will reuse the byline of your latest article that has one." tag="paragraph" />
        ) : (
          <ModalLine text="Articles will be published without a byline." tag="paragraph" />
        )}
      </Modal>
    </>
  );
};

export default AutopilotPanel;
